import { defaultsConfig } from "~config/sport/defaults";
import { hockeyUiConfig } from "~config/sport/hockeyUi";
import { Disciplines } from "~enums/disciplines";
import { FixtureStatuses } from "~enums/fixtureStatuses";
import { PeriodTypes } from "~enums/periodTypes.ts";
import { Roles } from "~enums/roles";
import { RosterPosition } from "~enums/rosterPosition";
import { SeasonTypes } from "~enums/seasonTypes";
import { Standards } from "~enums/standards";
import { UniformItemBottomTypes } from "~enums/uniformItemBottomTypes";
import { UniformItemColors } from "~enums/uniformItemColors.ts";
import { UniformItemTopTypes } from "~enums/uniformItemTopTypes";
import { UniformItemTypes } from "~enums/uniformItemTypes";
import type { SportConfiguration } from "~types/sportConfiguration";

const seasonStandards: Standards[] = [
  Standards.Olympics,
  Standards.FihWorldCup,
  Standards.FihOlympicQualifiers,
  Standards.FihWorldCupQualifiers,
  Standards.ContinentalChampionship,
  Standards.FihProLeague,
  Standards.CommonwealthGames,
  Standards.FihNationsCup,
  Standards.ContinentalQualifier,
  Standards.ContinentalChampionshipDiv2,
  Standards.ContinentalChampionshipDiv3,
  Standards.ContinentalChampionshipDiv4,
  Standards.InvitationalTournament,
  Standards.ApprovedTestMatches,
  Standards.WorldLeague,
  Standards.HockeySeriesOpen,
  Standards.HistoricalBaseline,
];

const seasonOfficialRoles: Array<Roles> = [
  Roles.AppealJury,
  Roles.AppealJuryPresident,
  Roles.TechnicalDelegateAssistant,
  Roles.UmpireAssistantManager,
  Roles.RepresentativeAssociation,
  Roles.RepresentativeAssociationContinental,
  Roles.RepresentativeContinentalFederation,
  Roles.RepresentativeFederation,
  Roles.MedicalOfficerFih,
  Roles.RepresentativeAssociationGoverningBody,
  Roles.RepresentativeGoverningBody,
  Roles.RepresentativeAssociationIntercontinental,
  Roles.RepresentativeIntercontinentalFederation,
  Roles.RepresentativeAssociationInternational,
  Roles.RepresentativeInternationalFederation,
  Roles.Judge,
  Roles.MatchManager,
  Roles.MedicalOfficer,
  Roles.MedicalOfficerCF,
  Roles.RepresentativeAssociationNational,
  Roles.RepresentativeFederationNational,
  Roles.NationalTournamentOfficial,
  Roles.UmpireNational,
  Roles.UmpireNeutral,
  Roles.RepresentativeAssociationRegional,
  Roles.RepresentativeRegionalFederation,
  Roles.TechnicalDelegate,
  Roles.TechnicalOfficer,
  Roles.TechnicalOfficial,
  Roles.AdministratorTournament,
  Roles.Umpire,
  Roles.UmpireManager,
];

const fixtureOfficialRoles: Array<Roles> = [
  Roles.MatchManager,
  Roles.UmpireReserve,
  Roles.JudgeScoring,
  Roles.TechnicalDelegate,
  Roles.TechnicalOfficial,
  Roles.JudgeTiming,
  Roles.Umpire,
  Roles.UmpireVideo,
];

const seasonStaffRoles: Array<Roles> = [
  Roles.CoachAssistant,
  Roles.Coach,
  Roles.Doctor,
  Roles.Manager,
  Roles.Other,
  Roles.PhysicalTrainer,
  Roles.Physiotherapist,
  Roles.Psychologist,
  Roles.StandInManager,
  Roles.TeamLeader,
  Roles.TeamMediaOfficer,
  Roles.Translator,
  Roles.VideoAnalyst,
];

const fixtureStaffRoles: Array<Roles> = [Roles.Coach, Roles.Doctor, Roles.Manager, Roles.Other, Roles.Physiotherapist];

const uniforms: SportConfiguration["uniforms"] = {
  bottomOptions: [UniformItemBottomTypes.Shorts, UniformItemBottomTypes.Skirts],
  items: [
    {
      titleIntlKey: "uniform.player",
      topType: [UniformItemTopTypes.ShortSleeve],
      itemTypes: {
        [UniformItemTypes.Top]: [UniformItemColors.Primary, UniformItemColors.Secondary],
        [UniformItemTypes.Bottom]: [UniformItemColors.Primary],
        [UniformItemTypes.Socks]: [UniformItemColors.Primary],
      },
    },
    {
      titleIntlKey: "uniform.goalkeeper",
      topType: [UniformItemTopTypes.LongSleeve],
      itemTypes: {
        [UniformItemTypes.GoalkeeperTop]: [UniformItemColors.Primary, UniformItemColors.Secondary],
        [UniformItemTypes.GoalkeeperBottom]: [UniformItemColors.Primary],
        [UniformItemTypes.Socks]: [UniformItemColors.Primary],
      },
    },
  ],
};

const seasonTypes = [
  SeasonTypes.Season,
  SeasonTypes.Tournament,
  SeasonTypes.OneOff,
  SeasonTypes.MultiYearHistorical,
  SeasonTypes.PreSeason,
];

const rosterPositions = [
  RosterPosition.AttackingMidfield,
  RosterPosition.CentreForward,
  RosterPosition.CentreHalf,
  RosterPosition.Defender,
  RosterPosition.DefensiveMidfield,
  RosterPosition.Forward,
  RosterPosition.FullBack,
  RosterPosition.GoalKeeper,
  RosterPosition.HalfBack,
  RosterPosition.LeftForward,
  RosterPosition.Midfielder,
  RosterPosition.Outfielder,
  RosterPosition.RightForward,
  RosterPosition.Striker,
  RosterPosition.Sweeper,
];

export const hockeyConfig: SportConfiguration = {
  ...defaultsConfig,
  fixtureStatuses: [...defaultsConfig.fixtureStatuses, FixtureStatuses.WarmUp],
  disciplines: [Disciplines.Indoor, Disciplines.Outdoor, Disciplines.Hockey5S, Disciplines.Parahockey],
  seasonStandards,
  entityStandards: [...defaultsConfig.entityStandards, Standards.WorldLeague, Standards.HockeySeriesOpen],
  uniforms,
  seasonOfficialRoles,
  fixtureOfficialRoles,
  seasonStaffRoles,
  fixtureStaffRoles,
  seasonTypes,
  rosterPositions,
  uiConfiguration: hockeyUiConfig,
  periods: {
    [PeriodTypes.Period1]: { intlKey: "period.period1" },
    [PeriodTypes.Period2]: { intlKey: "period.period2" },
    [PeriodTypes.Period3]: { intlKey: "period.period3" },
    [PeriodTypes.Period4]: { intlKey: "period.period4" },
    [PeriodTypes.ExtraPeriod0]: { intlKey: "period.extraTime1" },
    [PeriodTypes.ExtraPeriod1]: { intlKey: "period.extraTime2" },
    [PeriodTypes.ExtraPeriod2]: { intlKey: "period.shootout" },
    [PeriodTypes.ExtraPeriod3]: { intlKey: "period.extraTime4" },
  },
};
